import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { selectGroupGuid, selectUserGuid } from 'slices/commonSelectors';
import { useTropicalDemoKind } from 'utils/featureFlags';
import {
  useCreateOrgEventAutomationRule,
  useOrgEventAutomationRules,
  type CreateOrgEventAutomationRulePayload,
} from 'hooks/orgEvents/orgEventAutomationHooks';
import { NotificationType } from 'hooks/useNotification';
import {
  CHEMICAL_THREAT,
  EARTHQUAKE_THREAT,
  HADR_THREAT,
  JAPAN_CHEMICAL_RULE_NAME,
  JAPAN_EARTHQUAKE_RULE_NAME,
  JAPAN_EVENT_REGION,
  JAPAN_HADR_RULE_NAME,
  JAPAN_NEO_RULE_NAME,
  JAPAN_PORT_AIRPORT_RULE_NAME,
  NEO_THREAT,
  PORT_AIRPORT_THREAT,
} from '../NotificationsTab/japanEarthquakeNotification';

const ensureLocks = new Map<string, Promise<unknown>>();

export const JAPAN_SEEDED_RULES: Array<{
  name: string;
  type: string;
  thresholdDescription: string;
}> = [
  {
    name: JAPAN_EARTHQUAKE_RULE_NAME,
    type: EARTHQUAKE_THREAT,
    thresholdDescription:
      'JMA seismic intensity upper 6 or above at a defense critical infrastructure site',
  },
  {
    name: JAPAN_CHEMICAL_RULE_NAME,
    type: CHEMICAL_THREAT,
    thresholdDescription:
      'Hazardous chemical release with a plume over populated areas or transport nodes',
  },
  {
    name: JAPAN_PORT_AIRPORT_RULE_NAME,
    type: PORT_AIRPORT_THREAT,
    thresholdDescription:
      'Closure of a runway or port serving the response or the evacuation',
  },
  {
    name: JAPAN_HADR_RULE_NAME,
    type: HADR_THREAT,
    thresholdDescription:
      'Host nation request for humanitarian assistance and disaster relief',
  },
  {
    name: JAPAN_NEO_RULE_NAME,
    type: NEO_THREAT,
    thresholdDescription:
      'Embassy decision to assist the departure of US citizens',
  },
];

/**
 * Ensures the Japan group has one rule per threat so resource checklists can be
 * keyed by rule name.
 *
 * There is no live seismic or plume feed behind the demo, so these are seeded
 * with no source and disabled — they exist for the checklist keying, not to
 * fire.
 */
export function useEnsureJapanAutomationRule() {
  const japanEnabled = useTropicalDemoKind() === 'japan';
  const groupGuid = useSelector(selectGroupGuid);
  const userGuid = useSelector(selectUserGuid);
  const { data: rules, isSuccess } = useOrgEventAutomationRules({
    enabled: japanEnabled && !!groupGuid,
  });
  const createRule = useCreateOrgEventAutomationRule();
  const attempted = useRef(false);

  useEffect(() => {
    if (!japanEnabled || !groupGuid || !isSuccess || attempted.current) return;
    const existingNames = new Set(
      (rules ?? []).filter((rule) => !rule.archived).map((rule) => rule.name)
    );
    const missing = JAPAN_SEEDED_RULES.filter(
      (seed) => !existingNames.has(seed.name)
    );
    if (missing.length === 0) {
      attempted.current = true;
      return;
    }

    const lockKey = `japan-automation-rules:${groupGuid}`;
    if (ensureLocks.has(lockKey)) return;

    attempted.current = true;
    const notifyConfig = {
      notificationTypes: [NotificationType.IN_APP],
      userIds: userGuid ? [userGuid] : [],
      teamIds: [],
      priority: 'watch',
      titleTemplate: 'Threshold event: {eventName}',
      bodyTemplate: '{eventName} impacts {resourceCount} resources.',
    };

    const promise = Promise.all(
      missing.map((seed) => {
        const payload: CreateOrgEventAutomationRulePayload = {
          group_guid: groupGuid,
          name: seed.name,
          enabled: false,
          logic: 'all',
          source: undefined,
          conditions: [],
          event_template: {
            severity: 'High',
            type: seed.type,
            region: JAPAN_EVENT_REGION,
            thresholdDescription: seed.thresholdDescription,
          },
          notify_on_create: false,
          checklist_on_create: false,
          checklist_config: { items: [] },
          notify_config: notifyConfig,
          created_by_user_guid: userGuid ?? null,
        };
        return createRule.mutateAsync(payload);
      })
    )
      .catch((err) => {
        console.warn('[japan] failed to ensure automation rules', err);
        attempted.current = false;
      })
      .finally(() => {
        ensureLocks.delete(lockKey);
      });

    ensureLocks.set(lockKey, promise);
  }, [japanEnabled, groupGuid, userGuid, isSuccess, rules, createRule]);
}
